/**
 * @fileoverview Medication History component for displaying and managing patient medications
 * @module components/medication/medicationHistory
 * @requires react
 * @requires lodash
 * @requires @mui/material
 * @requires @mui/icons-material/KeyboardArrowDown
 * @requires @mui/icons-material/MoreVert
 * @requires ../../api/medicationHistory
 * @requires ../modals/EditRxModal
 * @requires ../modals/AddRxModal
 * @requires ../modals/RxHistoryModal
 * @requires ../modals/DiscontinueRxModal
 * @requires ../common/table/PaginationControls
 * @requires ../common/searchBar/CommonSearchBar
 * @requires ../../context/LoadingContext
 * @requires date-fns/format
 */

import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  fetchMedications,
  fetchMedicationHistory,
} from "../../api/medicationHistory";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Menu,
  MenuItem,
  // TextField removed (unused)
  Chip,
  Tooltip,
} from "@mui/material";
import FocusableScrollContainer from "../common/FocusableScrollContainer";

import RxHistoryModal from "../modals/RxHistoryModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import phoneIcon from "../../assets/Phone.svg";
import { useLoading } from "../../context/LoadingContext";
import { formatPhoneNumber } from "../../utils/formatUtils";

const isManualRx = (type) => String(type || "").toUpperCase() === "MANUAL RX";

/**
 * Returns a styled chip component based on medication status
 * @param {string} status - The status of the medication (Enrolled, Eligible, Declined)
 * @returns {JSX.Element} Styled Chip component
 */
const getStatusChip = (status) => {
  switch (status) {
    case "Enrolled":
      return <Chip label="Enrolled" color="success" size="small" />;
    case "Eligible":
      return <Chip label="Eligible" color="warning" size="small" />;
    case "Declined":
      return <Chip label="Declined" color="error" size="small" />;
    default:
      return null;
  }
};

function formatToMMDDYYYY(dateStr) {
  if (!dateStr) return "";

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  }

  return dateStr;
}

const MedicationCard = ({ medication, onMenuOpen, onCardClick }) => {
  let hoverBackgroundColor = "#FFFFFF"; // default

  if (medication.medicationType === "Claims RX") {
    hoverBackgroundColor = "#f5f5f5";
  } else if (medication.discontinueRx) {
    hoverBackgroundColor = "#f0f0f0";
  }

  return (
    <Paper
      onClick={() => onCardClick(medication)}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: "1px solid #E8E8E9",
        boxShadow: "none",
        opacity: medication.discontinueRx ? 0.5 : 1,
        backgroundColor: medication.discontinueRx ? "#f0f0f0" : "#FFFFFF",
        cursor:
          medication.medicationType === "Claims RX" ? "pointer" : "default",
        "&:hover": {
          backgroundColor: hoverBackgroundColor,
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box flex={1} minWidth={0}>
          <Typography
            sx={{
              color: "#403C6F",
              fontWeight: 700,
              fontSize: "1rem",
              wordBreak: "break-word",
            }}
          >
            {medication.drug}
          </Typography>
          <Typography
            sx={{ color: "#66638C", fontWeight: 600, fontSize: "0.875rem" }}
          >
            {medication.strength}
          </Typography>
          <Typography
            sx={{
              color: "#666666",
              fontSize: "0.875rem",
              mt: 0.5,
              wordBreak: "break-word",
            }}
          >
            {medication.instructions.split("|")[0].trim()}
          </Typography>
        </Box>
        <IconButton
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click from firing
            onMenuOpen(e, medication.id);
          }}
          size="small"
          disabled={medication.discontinueRx}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Grid container spacing={2} rowSpacing={1}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Member Cost
          </Typography>
          <Typography sx={{ fontWeight: 500 }}>${medication.cost}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Last Filled
          </Typography>
          <Typography sx={{ fontWeight: 500 }}>
            {medication.lastFilled}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

MedicationCard.propTypes = {
  medication: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    medicationType: PropTypes.string,
    discontinueRx: PropTypes.bool,
    drug: PropTypes.string,
    strength: PropTypes.string,
    instructions: PropTypes.string,
    cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lastFilled: PropTypes.string,
  }).isRequired,
  onMenuOpen: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
};

// Helper: Category Icons

/**
 * MedicationHistory Component
 *
 * Displays and manages a patient's medication history with CRUD operations.
 * Includes functionality to view, add, edit, and discontinue medications.
 *
 * @param {Object} props - Component props
 * @param {string|number} props.memberId - The ID of the member/patient
 * @returns {JSX.Element} The rendered medication history component
 */
const MedicationHistory = ({
  memberId,
  canWrite = true,
  canDelete = true,
  pageSize = 30,
  setPageSize = () => {},
  onLoadingChange,
  onCountChange,
  isShowPhiEnabled = false,
}) => {
  // Default memberId for demo purposes, should be passed from parent
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [page, setPage] = useState(0);
  const isFetchingMoreRef = useRef(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  const [isRxHistoryModalOpen, setIsRxHistoryModalOpen] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [selectedMedicationName, setSelectedMedicationName] = useState("");
  const [formatData, setFormatData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const formatDataRef = useRef([]);
  const totalElementsRef = useRef(0);
  const { showLoading, hideLoading } = useLoading({
    showLoading: () => {},
    hideLoading: () => {},
  });

  const [rxHistoryData, setRxHistoryData] = useState({
    content: [],
    totalElements: 0,
  });
  const [rxHistoryCurrentPage, setRxHistoryCurrentPage] = useState(0);
  const [rxHistoryTotalRows, setRxHistoryTotalRows] = useState(0);
  const [rxHistoryLoading, setRxHistoryLoading] = useState(false);
  const [rxHistoryLoadingMore, setRxHistoryLoadingMore] = useState(false);
  const rxHistoryTableRef = useRef(null);
  const isFetchingMoreRxHistoryRef = useRef(false);
  const selectedMedicationForHistoryRef = useRef(null);
  const rxHistoryAllDataRef = useRef(null);

  const tableContainerRef = useRef(null);
  const [searchText] = useState("");

  const loadMedications = useCallback(
    async (searchQuery = "", pageNum = 0) => {
      try {
        setIsLoading(true);

        if (pageNum > 0) {
          isFetchingMoreRef.current = true;
        } else {
          showLoading();
        }

        const data = await fetchMedications(
          memberId,
          searchQuery,
          pageNum,
          pageSize,
        );
        const formatted = (data.content || []).map((medication) => {
          // Extract provider logic
          let drug = "";

          if (medication.medicationName) {
            if (isManualRx(medication.medicationType)) {
              drug = medication.medicationName;
            } else {
              // Capitalize first letter
              drug =
                medication.medicationName.charAt(0).toUpperCase() +
                medication.medicationName.slice(1).toLowerCase();
            }
          }
          let formattedProvider = "N/A";
          if (
            medication.providerName &&
            medication.providerName !== "-" &&
            medication.providerName !== "N/a" &&
            medication.providerName !== "N/A"
          ) {
            formattedProvider = isManualRx(medication.medicationType)
              ? medication.providerName
              : medication.providerName
                  .toLowerCase()
                  .replaceAll(/(^\w)|(\s\w)/g, (c) => c.toUpperCase());
          }

          // Extract pharmacy logic
          let formattedPharmacy = "N/A";
          if (
            medication.pharmacyName &&
            medication.pharmacyName !== "-" &&
            medication.pharmacyName !== "N/a" &&
            medication.pharmacyName !== "N/A"
          ) {
            formattedPharmacy = isManualRx(medication.medicationType)
              ? medication.pharmacyName
              : medication.pharmacyName
                  .toLowerCase()
                  .replaceAll(/(^\w)|(\s\w)/g, (c) => c.toUpperCase());
          }

          return {
            id: medication.id,
            category: "Biosimilars",
            drug: drug,
            daySupply: medication.daysOfSupply || "N/A",
            instructions: `${
              isManualRx(medication?.medicationType)
                ? `${medication?.sig || "N/A"}`
                : `Quantity: ${medication?.metricQuantity || "N/A"}`
            } | Days Supply: ${medication?.daysOfSupply || "N/A"}`,
            cost: medication.memberCost
              ? `${new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(medication.memberCost)}`
              : "0.00",
            lastFilled: formatToMMDDYYYY(medication.lastFilled) || "N/A",
            provider: formattedProvider,
            phone: medication.providerPhone ?? "",
            pharmacy: formattedPharmacy,
            pharmacyPhone: medication.pharmacyPhone ?? "",
            dueDate: medication.dateOfNearestExpiry || "",
            pdc: medication.pdc || "",
            medicationType: medication.medicationType || "",
            strength: medication.strength ?? "",
            discontinueRx: medication.discontinued || false,
          };
        });

        const sortedMedications = [...formatted].sort((a, b) => {
          if (a.discontinueRx && !b.discontinueRx) return 1;
          if (!a.discontinueRx && b.discontinueRx) return -1;
          return 0;
        });

        if (pageNum > 0) {
          setFormatData((prev) => {
            const combined = [...(prev || []), ...(sortedMedications || [])];
            const map = new Map();
            for (const item of combined) {
              if (item?.id) {
                map.set(item.id, item);
              }
            }
            const result = Array.from(map.values());
            formatDataRef.current = result;
            return result;
          });
        } else {
          setFormatData(sortedMedications);
          formatDataRef.current = sortedMedications;
        }

        const totalElementsValue = data.totalElements || 0;
        totalElementsRef.current = totalElementsValue;
        if (typeof onCountChange === "function") {
          onCountChange(totalElementsValue);
        }
      } catch (error) {
        console.error("Unexpected error", error);
        hideLoading();
        setFormatData([]);
      } finally {
        if (pageNum > 0) {
          isFetchingMoreRef.current = false;
        } else {
          hideLoading();
        }
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [memberId, showLoading, hideLoading, pageSize],
  );

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  useEffect(() => {
    setPage(0);
    setFormatData([]);
    formatDataRef.current = [];
    totalElementsRef.current = 0;
    isFetchingMoreRef.current = false;
    loadMedications(searchText, 0);
    /* eslint-disable no-unused-vars */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [searchText, memberId, pageSize]);

  useEffect(() => {
    if (page === 0) return;
    loadMedications(searchText, page);
  }, [page, searchText, loadMedications]);

  const handleScroll = useCallback(
    (e) => {
      try {
        const target = e.target;

        if (isLoading || isFetchingMoreRef.current) return;

        const currentLength = formatDataRef.current?.length || 0;
        const totalRows = totalElementsRef.current || 0;
        const canLoadMore = currentLength < totalRows;
        if (!canLoadMore) return;

        const threshold = 200;
        const scrollBottom =
          target.scrollHeight - target.scrollTop - target.clientHeight;

        if (scrollBottom <= threshold) {
          if (isFetchingMoreRef.current) return;
          isFetchingMoreRef.current = true;

          setPage((p) => p + 1);
        }
      } catch (error) {
        console.error("Unexpected error", error);
      }
    },
    [isLoading],
  );

  const handleMenuOpen = (event, rowId) => {
    // Find the row data
    const rowData = formatData.find((item) => item.id === rowId);

    // Don't open menu if the row is discontinued
    if (rowData?.discontinueRx === true) {
      return;
    }
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
    setSelectedMenuItem(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
    setSelectedMenuItem(null);
  };

  const handleMenuItemClick = async (menuItem) => {
    // Block edit when user lacks write permission
    if (!canWrite && menuItem === "Edit Rx") {
      handleMenuClose();
      return;
    }
    // Block discontinue when user lacks delete permission
    if (!canDelete && menuItem === "Discontinue Rx") {
      handleMenuClose();
      return;
    }
    setSelectedMenuItem(menuItem);

    if (menuItem === "Edit Rx" && menuRowId) {
      // Find the medication data in formatData by id
      const hasMedicationToEdit = formatData.some(
        (item) => item.id === menuRowId,
      );

      if (hasMedicationToEdit) {
        console.log("In if block");
      }
    } else if (menuItem === "Discontinue Rx" && menuRowId) {
      setSelectedMedications([menuRowId]);
    } else if (menuItem === "Rx History" && menuRowId) {
      const selectedMedication = formatData.find(
        (item) => item.id === menuRowId,
      );
      if (selectedMedication) {
        setSelectedMedicationName(selectedMedication.drug);
        selectedMedicationForHistoryRef.current = {
          id: selectedMedication.id,
          medicationType: selectedMedication.medicationType,
        };
        setRxHistoryCurrentPage(0);
        setRxHistoryData({ content: [], totalElements: 0 });
        setRxHistoryTotalRows(0);
        setRxHistoryLoadingMore(false);
        setIsRxHistoryModalOpen(true);
      }
    }

    handleMenuClose();
  };

  const handleRowClick = async (row) => {
    if (
      row.medicationType !== "Claims RX" &&
      row.medicationType !== "RX Reject Transaction"
    )
      return;

    setSelectedMedicationName(row.drug);
    selectedMedicationForHistoryRef.current = {
      id: row.id,
      medicationType: row.medicationType,
    };
    setRxHistoryCurrentPage(0);
    setRxHistoryData({ content: [], totalElements: 0 });
    setRxHistoryTotalRows(0);
    setRxHistoryLoadingMore(false);
    setIsRxHistoryModalOpen(true);
  };

  // Helper: Set pagination loading state
  const setPaginationLoadingState = useCallback((page) => {
    setRxHistoryLoading(true);
    if (page > 0) {
      isFetchingMoreRxHistoryRef.current = true;
      setRxHistoryLoadingMore(true);
    }
  }, []);

  // Helper: Handle stored data pagination
  const handleStoredDataPagination = useCallback((page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedContent = rxHistoryAllDataRef.current.slice(
      startIndex,
      endIndex,
    );
    const normalized = {
      content: paginatedContent,
      totalElements: rxHistoryAllDataRef.current.length,
    };

    setRxHistoryData((prev) => ({
      content: [...(prev?.content || []), ...(normalized.content || [])],
      totalElements: normalized.totalElements,
    }));
    setRxHistoryTotalRows(normalized.totalElements);
    setRxHistoryLoading(false);
    isFetchingMoreRxHistoryRef.current = false;
    setRxHistoryLoadingMore(false);
  }, []);

  // Helper: Normalize array response for first page
  const normalizeArrayResponseForFirstPage = useCallback((response, size) => {
    if (response.length > size) {
      // API returned all records - show all since they're already loaded
      rxHistoryAllDataRef.current = response;
      return {
        content: response,
        totalElements: response.length,
      };
    }
    if (response.length === size) {
      // API returned exactly 'size' records - might be paginated
      rxHistoryAllDataRef.current = response;
      return {
        content: response,
        totalElements: response.length,
      };
    }
    // API returned less than 'size' - all records
    return {
      content: response,
      totalElements: response.length,
    };
  }, []);

  // Helper: Normalize array response for subsequent pages
  const normalizeArrayResponseForSubsequentPages = useCallback((response) => {
    if (rxHistoryAllDataRef.current) {
      const combined = [...rxHistoryAllDataRef.current, ...response];
      rxHistoryAllDataRef.current = combined;
      return {
        content: combined,
        totalElements: combined.length,
      };
    }
    return {
      content: response,
      totalElements: response.length,
    };
  }, []);

  // Helper: Normalize array response
  const normalizeArrayResponse = useCallback(
    (response, page, size) => {
      if (page === 0) {
        return normalizeArrayResponseForFirstPage(response, size);
      }
      return normalizeArrayResponseForSubsequentPages(response);
    },
    [
      normalizeArrayResponseForFirstPage,
      normalizeArrayResponseForSubsequentPages,
    ],
  );

  // Helper: Normalize object response
  const normalizeObjectResponse = useCallback((response) => {
    return {
      content: Array.isArray(response.content) ? response.content : [],
      totalElements:
        typeof response.totalElements === "number"
          ? response.totalElements
          : response.content?.length || 0,
    };
  }, []);

  // Helper: Normalize fallback response
  const normalizeFallbackResponse = useCallback((response) => {
    return {
      content: response ? [response] : [],
      totalElements: response ? 1 : 0,
    };
  }, []);

  // Helper: Normalize API response
  const normalizeApiResponse = useCallback(
    (response, page, size) => {
      if (Array.isArray(response)) {
        return normalizeArrayResponse(response, page, size);
      }
      if (response?.content) {
        return normalizeObjectResponse(response);
      }
      return normalizeFallbackResponse(response);
    },
    [
      normalizeArrayResponse,
      normalizeObjectResponse,
      normalizeFallbackResponse,
    ],
  );

  // Helper: Get total elements value
  const getTotalElementsValue = useCallback((normalized) => {
    if (typeof normalized.totalElements === "number") {
      return normalized.totalElements;
    }
    return normalized.content?.length || 0;
  }, []);

  // Helper: Update Rx history data based on page
  const updateRxHistoryData = useCallback((normalized, page) => {
    if (page === 0) {
      setRxHistoryData(normalized);
    } else {
      setRxHistoryData((prev) => ({
        content: [...(prev?.content || []), ...(normalized.content || [])],
        totalElements:
          typeof normalized.totalElements === "number"
            ? normalized.totalElements
            : prev?.totalElements || 0,
      }));
    }
  }, []);

  // Load Rx History data (same pattern as membersPage loadInitialData)
  const loadRxHistoryData = useCallback(
    async (page = 0, size = 30) => {
      if (!selectedMedicationForHistoryRef.current) return;

      try {
        setPaginationLoadingState(page);

        // If we have stored data and requesting page > 0, use stored data for client-side pagination
        if (page > 0 && rxHistoryAllDataRef.current) {
          handleStoredDataPagination(page, size);
          return;
        }

        const response = await fetchMedicationHistory(
          memberId,
          {
            id: selectedMedicationForHistoryRef.current.id,
            medicationType:
              selectedMedicationForHistoryRef.current.medicationType,
          },
          page,
          size,
        );

        const normalized = normalizeApiResponse(response, page, size);
        updateRxHistoryData(normalized, page);

        const totalElementsValue = getTotalElementsValue(normalized);
        setRxHistoryTotalRows(totalElementsValue);
        setRxHistoryLoading(false);
      } catch (error) {
        console.error("Unexpected error", error);
        setRxHistoryData({ content: [], totalElements: 0 });
        setRxHistoryTotalRows(0);
        setRxHistoryLoading(false);
      } finally {
        isFetchingMoreRxHistoryRef.current = false;
        setRxHistoryLoadingMore(false);
      }
    },
    [
      memberId,
      setPaginationLoadingState,
      handleStoredDataPagination,
      normalizeApiResponse,
      updateRxHistoryData,
      getTotalElementsValue,
    ],
  );

  useEffect(() => {
    if (
      isRxHistoryModalOpen &&
      rxHistoryCurrentPage === 0 &&
      selectedMedicationForHistoryRef.current
    ) {
      rxHistoryAllDataRef.current = null;
      loadRxHistoryData(0, pageSize);
    }
    /* eslint-disable no-unused-vars */
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isRxHistoryModalOpen, loadRxHistoryData, pageSize]);

  // Helper: Check if table element is scrollable
  const isTableScrollable = useCallback((el) => {
    if (!el) return false;
    return el.scrollHeight > el.clientHeight;
  }, []);

  // Helper: Check if conditions are met for auto-loading
  const shouldAutoLoadData = useCallback(() => {
    return (
      isRxHistoryModalOpen &&
      rxHistoryAllDataRef.current &&
      rxHistoryData?.content?.length < rxHistoryTotalRows &&
      !rxHistoryLoading &&
      !isFetchingMoreRxHistoryRef.current &&
      rxHistoryCurrentPage === 0
    );
  }, [
    isRxHistoryModalOpen,
    rxHistoryData?.content?.length,
    rxHistoryTotalRows,
    rxHistoryLoading,
    rxHistoryCurrentPage,
  ]);

  // Helper: Load next page if table is not scrollable
  const loadNextPageIfNotScrollable = useCallback(() => {
    const el = rxHistoryTableRef.current;
    if (!el) return;

    const isScrollable = isTableScrollable(el);
    const hasMoreData = rxHistoryData?.content?.length < rxHistoryTotalRows;

    if (!isScrollable && hasMoreData) {
      setTimeout(() => {
        setRxHistoryCurrentPage(1);
      }, 100);
    }
  }, [isTableScrollable, rxHistoryData?.content?.length, rxHistoryTotalRows]);

  // Auto-load remaining data if we have stored data and table might not be scrollable
  useEffect(() => {
    if (shouldAutoLoadData()) {
      loadNextPageIfNotScrollable();
    }
  }, [shouldAutoLoadData, loadNextPageIfNotScrollable]);

  useEffect(() => {
    if (rxHistoryCurrentPage === 0 || !isRxHistoryModalOpen) return;
    loadRxHistoryData(rxHistoryCurrentPage, pageSize);
  }, [rxHistoryCurrentPage, loadRxHistoryData, pageSize, isRxHistoryModalOpen]);

  // Helper: Check if can load more data
  const canLoadMoreData = useCallback(() => {
    if (rxHistoryLoading || isFetchingMoreRxHistoryRef.current) return false;
    const currentLength = rxHistoryData?.content?.length || 0;
    const totalRows = rxHistoryTotalRows || 0;
    return currentLength < totalRows;
  }, [rxHistoryLoading, rxHistoryData?.content?.length, rxHistoryTotalRows]);

  // Helper: Check if scroll is near bottom
  const isScrollNearBottom = useCallback((el, threshold = 200) => {
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }, []);

  // Helper: Handle scroll event for RX history
  const handleRxHistoryScroll = useCallback(
    (el) => {
      if (!canLoadMoreData()) return;
      if (!isScrollNearBottom(el)) return;

      isFetchingMoreRxHistoryRef.current = true;
      setRxHistoryCurrentPage((p) => p + 1);
    },
    [canLoadMoreData, isScrollNearBottom],
  );

  // Helper: Create scroll event listener
  const createScrollListener = useCallback(
    (el) => {
      const onScroll = () => handleRxHistoryScroll(el);
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        el.removeEventListener("scroll", onScroll);
      };
    },
    [handleRxHistoryScroll],
  );

  // Helper: Check if ref is ready and setup scroll listener
  const checkRefAndSetupScroll = useCallback(
    (cleanupRef) => {
      const el = rxHistoryTableRef.current;
      if (!el) {
        const timeoutId = setTimeout(() => {
          checkRefAndSetupScroll(cleanupRef);
        }, 100);
        cleanupRef.current = () => clearTimeout(timeoutId);
        return;
      }
      const scrollCleanup = createScrollListener(el);
      cleanupRef.current = scrollCleanup;
    },
    [createScrollListener],
  );

  // Infinite scroll: listen to table scroll and load next page near bottom (same as membersPage)
  useEffect(() => {
    if (!isRxHistoryModalOpen) return;

    const cleanupRef = { current: null };

    // Start checking for ref
    const timeoutId = setTimeout(() => {
      checkRefAndSetupScroll(cleanupRef);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [isRxHistoryModalOpen, checkRefAndSetupScroll]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Paper
        sx={{
          // p: 1,
          pb: 2,
          borderRadius: 2,
          boxShadow: "none",
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box />
        {/* Search + Buttons Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            "& > *": {
              flexShrink: 0,
              marginBottom: 1,
            },
          }}
        >
          <Box
            sx={{ flex: "1 1 300px", maxWidth: "400px", minWidth: "250px" }}
          ></Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          ></Box>
        </Box>

        {/* Table (scrollable, keyboard focusable) */}
        {isMobile ? (
          <FocusableScrollContainer
            ref={tableContainerRef}
            component="section"
            aria-label="Scrollable medication history table"
            tabIndex={0}
            onScroll={handleScroll}
            sx={{
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              p: 0.5,
            }}
          >
            {formatData.length > 0 ? (
              formatData.map((row) => (
                <MedicationCard
                  key={row.id}
                  medication={row}
                  onMenuOpen={handleMenuOpen}
                  onCardClick={handleRowClick}
                />
              ))
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="150px"
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#0E0D09", fontSize: "16px" }}
                >
                  No records found
                </Typography>
              </Box>
            )}
          </FocusableScrollContainer>
        ) : (
          <TableContainer
            ref={tableContainerRef}
            component="section"
            aria-label="Scrollable medication history table"
            tabIndex={0}
            onScroll={handleScroll}
            sx={{
              width: "100%",
              flex: 1,
              height: "100%",
              minHeight: 0,
              borderRadius: "8px",
              boxShadow: "none",
              border: "1px solid #E8E8E9",
              overflowY: "auto", // This handles vertical scrolling
              overflowX: "auto", // This will enable horizontal scrolling on smaller screens
              outline: "none",
              "&:focus-visible": {
                boxShadow: "0 0 0 2px #403C6F",
              },
              "&::-webkit-scrollbar": {
                width: "6px",
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#F5F5F5",
                borderRadius: "4px",
                margin: "4px 0",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#A7A5BD",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#8E8CA8",
              },
              scrollbarWidth: "thin",
              scrollbarColor: "#A7A5BD #F5F5F5",
            }}
          >
            <Table
              stickyHeader
              sx={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "1100px",
                "& .MuiTableRow-root": {
                  position: "relative",
                  "&:not(.MuiTableRow-head)": {
                    borderBottom: "none",
                    "&:hover": {
                      backgroundColor: "#f9f9f9",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "1px",
                      backgroundColor: "#E8E8E9",
                    },
                  },
                },
                "& .MuiTableCell-root": {
                  borderBottom: "none",
                  height: "42px",
                  padding: "12px 16px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
                // Sticky header styling - matches members page pattern
                "& .MuiTableHead-root .MuiTableCell-root": {
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#CFCEDA",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    height: "32px", // Reduced header row height
                    "& > th": {
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                      padding: "0 16px",
                      lineHeight: 1.2,
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                      fontSize: "16px", // Added font size
                    },
                    "&:hover": {
                      backgroundColor: "#CFCEDA",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                      fontWeight: 550,
                      padding: "8px 16px",
                      borderBottom: "none",
                      fontSize: "16px",
                      width: "30%", // Medications column takes 30% width
                      maxWidth: "30%",
                    }}
                  >
                    Medications
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                      fontWeight: 550,
                      padding: "8px 16px",
                      borderBottom: "none",
                      fontSize: "16px",
                      width: "15%", // Member Cost column takes 15% width
                      maxWidth: "15%",
                    }}
                  >
                    Member Cost
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                      fontWeight: 550,
                      padding: "8px 16px",
                      borderBottom: "none",
                      fontSize: "16px",
                      width: "15%", // Last Filled column takes 15% width
                      maxWidth: "15%",
                    }}
                  >
                    Last Filled
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                      fontWeight: 550,
                      padding: "8px 16px",
                      borderBottom: "none",
                      fontSize: "16px",
                      width: "20%", // Provider column takes 20% width
                      maxWidth: "20%",
                    }}
                  >
                    Provider
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#CFCEDA",
                      color: "#403C6F",
                      fontWeight: 550,
                      padding: "8px 16px",
                      borderBottom: "none",
                      fontSize: "16px",
                      width: "20%", // Pharmacy column takes 20% width
                      maxWidth: "20%",
                    }}
                  >
                    Pharmacy
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formatData.map((row) => {
                  let rowOpacity = 1;
                  if (row.discontinueRx) {
                    rowOpacity = 0.5;
                  } else if (selectedMedications.includes(row.id)) {
                    rowOpacity = 0.7;
                  }

                  // Determine background color
                  let rowBackgroundColor = "transparent";
                  if (row.discontinueRx) {
                    rowBackgroundColor = "#f0f0f0";
                  } else if (selectedMedications.includes(row.id)) {
                    rowBackgroundColor = "#F7F8FB";
                  }
                  return (
                    <TableRow
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        opacity: { rowOpacity },
                        backgroundColor: { rowBackgroundColor },
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          cursor: "pointer",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          width: "30%",
                          maxWidth: "30%",
                          fontSize: "14px",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center" // center vertically in cell
                          height="100%"
                          gap={0.5}
                        >
                          {/* Drug name + Chip */}
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: "#403C6F",
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "calc(100% - 80px)", // leave room for chip
                                fontSize: "16px",
                              }}
                            >
                              <Tooltip
                                title={row.drug}
                                placement="bottom-start"
                                arrow
                                disableInteractive
                                slotProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiTooltip-tooltip": {
                                        backgroundColor: "#1c1c1f",
                                        color: "#FFFFFF",
                                        margin: 0,
                                        fontSize: "12px",
                                        lineHeight: 1.3,
                                      },
                                      "& .MuiTooltip-arrow": {
                                        color: "#1c1c1f",
                                      },
                                    },
                                  },
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {row.drug}
                                  </span>
                                  {(() => {
                                    const rawStrength = (
                                      row.strength || ""
                                    ).trim();
                                    if (!rawStrength) return null;
                                    const needsTruncate =
                                      rawStrength.length > 20;
                                    const displayStrength = needsTruncate
                                      ? rawStrength.slice(0, 20) + "…"
                                      : rawStrength;
                                    const strengthContent = (
                                      <span
                                        style={{
                                          color: "#66638C",
                                          fontWeight: 600,
                                          fontSize: "16px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {" | "}
                                        {displayStrength}
                                      </span>
                                    );
                                    return needsTruncate ? (
                                      <Tooltip
                                        title={rawStrength}
                                        placement="bottom-start"
                                        arrow
                                        disableInteractive
                                        slotProps={{
                                          popper: {
                                            sx: {
                                              "& .MuiTooltip-tooltip": {
                                                backgroundColor: "#1c1c1f",
                                                color: "#FFFFFF",
                                                margin: 0,
                                                fontSize: "12px",
                                                lineHeight: 1.3,
                                              },
                                              "& .MuiTooltip-arrow": {
                                                color: "#1c1c1f",
                                              },
                                            },
                                          },
                                        }}
                                      >
                                        {strengthContent}
                                      </Tooltip>
                                    ) : (
                                      strengthContent
                                    );
                                  })()}
                                </div>
                              </Tooltip>
                            </Typography>
                            {/* Removed Manual RX chip as per requirements */}
                          </Box>

                          {/* Instructions */}
                          {(() => {
                            const full = row.instructions || "";
                            // Split into instruction part and the rest (day supply)
                            const [instrPartRaw, ...restParts] =
                              full.split("|");
                            const instrPart = (instrPartRaw || "").trim();
                            const suffix = restParts.length
                              ? " |" + restParts.join("|")
                              : ""; // includes existing spacing and content like " Days Supply: X"
                            const needsTruncate = instrPart.length > 20;
                            const displayInstr = needsTruncate
                              ? instrPart.slice(0, 20) + "…"
                              : instrPart;
                            return (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#666666",
                                  fontSize: "14px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Tooltip
                                  title={needsTruncate ? instrPart : ""}
                                  placement="bottom-start"
                                  arrow
                                  describeChild
                                  disableInteractive
                                  slotProps={{
                                    popper: {
                                      sx: {
                                        "& .MuiTooltip-tooltip": {
                                          backgroundColor: "#1c1c1f",
                                          color: "#FFFFFF",
                                          margin: 0,
                                          fontSize: "12px",
                                          lineHeight: 1.3,
                                        },
                                        "& .MuiTooltip-arrow": {
                                          color: "#1c1c1f",
                                          margin: 0,
                                          left: "50% !important",
                                          transform:
                                            "translateX(-50%) !important",
                                        },
                                      },
                                    },
                                    tooltip: {
                                      sx: {
                                        margin: 0,
                                        marginTop: "0px !important",
                                        padding: "4px 8px",
                                      },
                                    },
                                  }}
                                >
                                  <span
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      display: "inline-block",
                                      maxWidth: "100%",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    {displayInstr}
                                  </span>
                                </Tooltip>
                                {suffix && (
                                  <span
                                    style={{
                                      marginLeft: 4,
                                      whiteSpace: "nowrap",
                                      verticalAlign: "middle",
                                    }}
                                  >
                                    {suffix.trimStart()}
                                  </span>
                                )}
                              </Typography>
                            );
                          })()}

                          {/* Status Chip */}
                          <Box>{getStatusChip(row.status)}</Box>
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{ fontSize: "16px", width: "20%", maxWidth: "20%" }}
                      >
                        {row.cost === null ? "" : `$${row.cost}`}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          fontSize: "16px",
                          width: "15%",
                          maxWidth: "15%",
                        }}
                      >
                        {row.lastFilled}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "16px", width: "20%", maxWidth: "20%" }}
                      >
                        {(() => {
                          const providerFull = row.provider || "-";
                          const needsTruncate =
                            providerFull !== "-" && providerFull.length > 15;
                          const providerDisplay = needsTruncate
                            ? providerFull.slice(0, 15) + "…"
                            : providerFull;
                          return (
                            <Typography
                              component="div"
                              sx={{
                                fontSize: "16px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {needsTruncate ? (
                                <Tooltip
                                  title={providerFull}
                                  placement="bottom-start"
                                  arrow
                                  disableInteractive
                                  slotProps={{
                                    popper: {
                                      sx: {
                                        "& .MuiTooltip-tooltip": {
                                          backgroundColor: "#1c1c1f",
                                          color: "#FFFFFF",
                                          fontSize: "12px",
                                          lineHeight: 1.3,
                                        },
                                        "& .MuiTooltip-arrow": {
                                          color: "#1c1c1f",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <span>{providerDisplay}</span>
                                </Tooltip>
                              ) : (
                                <span>{providerDisplay}</span>
                              )}
                            </Typography>
                          );
                        })()}
                        <div style={{ whiteSpace: "nowrap", fontSize: "16px" }}>
                          <Tooltip title="Phone" arrow>
                            <img
                              src={phoneIcon}
                              alt="Phone"
                              style={{ marginRight: "4px" }}
                            />
                          </Tooltip>
                          {formatPhoneNumber(row.phone, isShowPhiEnabled) ||
                            "N/A"}
                        </div>
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: "16px", width: "20%", maxWidth: "20%" }}
                      >
                        {(() => {
                          const pharmacyFull = row.pharmacy || "-";
                          const needsTruncate =
                            pharmacyFull !== "-" && pharmacyFull.length > 15;
                          const pharmacyDisplay = needsTruncate
                            ? pharmacyFull.slice(0, 15) + "…"
                            : pharmacyFull;
                          return (
                            <Typography
                              component="div"
                              sx={{
                                fontSize: "16px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "180px",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {needsTruncate ? (
                                <Tooltip
                                  title={pharmacyFull}
                                  placement="bottom-start"
                                  arrow
                                  disableInteractive
                                  slotProps={{
                                    popper: {
                                      sx: {
                                        "& .MuiTooltip-tooltip": {
                                          backgroundColor: "#1c1c1f",
                                          color: "#FFFFFF",
                                          fontSize: "12px",
                                          lineHeight: 1.3,
                                        },
                                        "& .MuiTooltip-arrow": {
                                          color: "#1c1c1f",
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <span>{pharmacyDisplay}</span>
                                </Tooltip>
                              ) : (
                                <span>{pharmacyDisplay}</span>
                              )}
                            </Typography>
                          );
                        })()}
                        <div style={{ whiteSpace: "nowrap", fontSize: "16px" }}>
                          <Tooltip title="Phone" arrow>
                            <img
                              src={phoneIcon}
                              alt="Phone"
                              style={{ marginRight: "4px" }}
                            />
                          </Tooltip>
                          {formatPhoneNumber(
                            row.pharmacyPhone,
                            isShowPhiEnabled,
                          ) || "N/A"}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {formatData.length === 0 && (
                  <TableRow style={{ height: "150px" }}>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{
                        verticalAlign: "middle",
                        textAlign: "center",
                        border: "none",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#0E0D09", fontSize: "16px" }}
                      >
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                width: "120px",
                minWidth: "120px",
                "& .MuiMenuItem-root": {
                  backgroundColor: "transparent",
                  color: "white",
                  borderRadius: "4px",
                  margin: "2px 4px",
                  minHeight: "28px",
                  fontSize: "0.75rem",
                  padding: "4px 8px",
                  "&.Mui-selected, &:active": {
                    backgroundColor: "#403C6F",
                    color: "white",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    color: "rgba(255, 255, 255, 0.5)",
                  },
                },
                "& .MuiMenu-list": {
                  padding: "2px",
                  backgroundColor: "#6E6D92",
                  borderRadius: "6px",
                  color: "white",
                },
                backgroundColor: "#6E6D92",
                backgroundImage: "none",
                boxShadow: "none",
                overflow: "hidden",
                "&::before, &::after": {
                  display: "none",
                },
              },
            },
          }}
        >
          {formatData.some(
            (item) => item.id === menuRowId && isManualRx(item.medicationType),
          ) && (
            <MenuItem
              onClick={() => handleMenuItemClick("Discontinue Rx")}
              disabled={!canDelete}
              sx={{
                "&.Mui-selected": { backgroundColor: "#403C6F !important" },
                "&.Mui-selected:hover": {
                  backgroundColor: "#403C6F !important",
                },
                opacity: canDelete ? 1 : 0.5,
                cursor: canDelete ? "pointer" : "not-allowed",
              }}
            >
              Discontinue Rx
            </MenuItem>
          )}
          <MenuItem
            onClick={() => handleMenuItemClick("Edit Rx")}
            selected={selectedMenuItem === "Edit Rx"}
            disabled={
              !canWrite ||
              !menuRowId ||
              !formatData.some(
                (item) =>
                  item.id === menuRowId && isManualRx(item.medicationType),
              )
            }
            sx={{
              "&.Mui-selected": { backgroundColor: "#403C6F !important" },
              "&.Mui-selected:hover": { backgroundColor: "#403C6F !important" },
              opacity: canWrite ? 1 : 0.5,
              cursor: canWrite ? "pointer" : "not-allowed",
            }}
          >
            Edit Rx
          </MenuItem>
          <MenuItem
            onClick={() => handleMenuItemClick("Rx History")}
            selected={selectedMenuItem === "Rx History"}
            disabled={
              !menuRowId ||
              !formatData.some(
                (item) =>
                  item.id === menuRowId && item.medicationType === "Claims RX",
              )
            }
            sx={{
              "&.Mui-selected": { backgroundColor: "#403C6F !important" },
              "&.Mui-selected:hover": { backgroundColor: "#403C6F !important" },
            }}
          >
            Rx History
          </MenuItem>
        </Menu>
      </Paper>

      <RxHistoryModal
        open={isRxHistoryModalOpen}
        loading={rxHistoryLoading}
        isLoadingMore={rxHistoryLoadingMore}
        tableRef={rxHistoryTableRef}
        isShowPhiEnabled={isShowPhiEnabled}
        onClose={() => {
          setIsRxHistoryModalOpen(false);
          setRxHistoryData({ content: [], totalElements: 0 });
          setSelectedMedicationName("");
          setRxHistoryCurrentPage(0);
          setRxHistoryTotalRows(0);
          setRxHistoryLoadingMore(false);
          selectedMedicationForHistoryRef.current = null;
          // Clear stored data
          rxHistoryAllDataRef.current = null;
        }}
        medicationName={selectedMedicationName}
        historyData={rxHistoryData?.content || []}
        type="medicationHistory"
        tableContainerProps={{
          sx: {
            maxHeight: 500,
            overflowY: "auto",
            width: "100%",
            minWidth: 600,
          },
        }}
      />
    </Box>
  );
};

MedicationHistory.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  canWrite: PropTypes.bool,
  canDelete: PropTypes.bool,
  pageSize: PropTypes.number,
  setPageSize: PropTypes.func,
  onLoadingChange: PropTypes.func,
  onCountChange: PropTypes.func,
  isShowPhiEnabled: PropTypes.bool,
};

export default MedicationHistory;
